export type PwaUpdateResult = 'updated' | 'up-to-date' | 'unsupported';

const waitUntilInstalled = (worker: ServiceWorker): Promise<void> => {
  if (worker.state === 'installed') return Promise.resolve();

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(
      () => reject(new Error('The update took too long to install.')),
      20_000
    );

    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed') {
        window.clearTimeout(timeout);
        resolve();
      } else if (worker.state === 'redundant') {
        window.clearTimeout(timeout);
        reject(new Error('The new app version could not be installed.'));
      }
    });
  });
};

const waitForControllerChange = (): Promise<void> =>
  new Promise(resolve => {
    const timeout = window.setTimeout(resolve, 8_000);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        window.clearTimeout(timeout);
        resolve();
      },
      { once: true }
    );
  });

export const updatePwa = async (): Promise<PwaUpdateResult> => {
  if (!('serviceWorker' in navigator)) return 'unsupported';

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return 'unsupported';

  await registration.update();

  const worker = registration.waiting || registration.installing;
  if (!worker) return 'up-to-date';

  await waitUntilInstalled(worker);

  const controllerChange = waitForControllerChange();
  worker.postMessage({ type: 'SKIP_WAITING' });
  await controllerChange;

  window.location.reload();
  return 'updated';
};
